import React from 'react';
import { useForm } from 'react-hook-form';
import LoadingButton from '@mui/lab/LoadingButton';
import classNames from 'classnames';
import ControlledTextField from '@/components/common/form/ControlledTextField';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';
import translate from '@/lib/intl/translate';
import type Label from '@/types/Label';
import joinNonEmptyParams from '@/utils/joinNonEmptyParams';
import { fetcherWithDataKey } from '@/utils/services';
import { useLabelManagementContext } from './LabelManagementContext';

const LABEL_NAME = 'labelName';
const defaultValues = { [LABEL_NAME]: '' };

function CreateLabelForm({ className = '' }: { className?: string }) {
  const ownerOrgId = useOwnerOrgId();
  const { create } = useLabelManagementContext();
  const {
    control,
    formState: { errors, isSubmitting, isValid },
    handleSubmit,
    reset,
    watch,
  } = useForm({ defaultValues, mode: 'all' });
  const watchedValue = watch(LABEL_NAME);

  const checkUniqueness = React.useCallback(
    async (inputVal: string): Promise<boolean | string> => {
      const val = inputVal.trim().toLowerCase();
      if (!val) return true;
      const data: Label[] = await fetcherWithDataKey('labels')(
        `labels?ownerOrgId=${joinNonEmptyParams({
          ownerOrgId,
          labelName: val,
        })}`,
      );
      const match = data.find(
        ({ labelName }) => labelName.toLowerCase() === val,
      );
      return match
        ? translate('LABEL_EDIT_ERROR_DUPLICATE', {
            labelName: match.labelName,
          })
        : true;
    },
    [ownerOrgId],
  );

  const onSubmit = React.useCallback(
    (e: React.FormEvent) =>
      handleSubmit(async (formData) => {
        await create({
          labelName: formData[LABEL_NAME].trim(),
          labelType: 'tag',
        });
        reset();
      })(e),
    [create, handleSubmit, reset],
  );

  return (
    <form onSubmit={onSubmit} className={classNames(className, 'flex gap-4')}>
      <ControlledTextField
        className="w-100 max-w-full"
        control={control}
        label={translate('COMPANY_LABEL_CREATE')}
        name={LABEL_NAME}
        required
        rules={{ validate: { checkUniqueness } }}
        error={!!errors[LABEL_NAME]}
        helperText={errors[LABEL_NAME]?.message}
      />
      <LoadingButton
        disabled={!watchedValue || !isValid}
        loading={isSubmitting}
        type="submit"
        variant="contained"
        className="h-12"
        size="small"
      >
        {translate('SAVE')}
      </LoadingButton>
    </form>
  );
}

export default CreateLabelForm;
